import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';

import { BackDropLoad } from './LoadingBackDrop';
import { Loading } from './Loading';
import { UserContext, SnackbarContext } from './userContext';
import {
  AccountHeader,
  EmptyBlock,
  Modal,
  PrimaryButton,
  GhostButton,
  Icon,
} from './myacc/primitives';

/* ─────────── status pill ─────────── */

function StatusPill({ status }) {
  const isActive = status === '1';
  return (
    <span className={`
      inline-flex items-center gap-1 rounded-full px-2 py-0.5
      text-[9.5px] font-bold uppercase tracking-[0.14em]
      ${isActive
        ? 'border border-amber/40 bg-amber/12 text-amber'
        : 'border border-border-subtle bg-surface-overlay/60 text-fg-tertiary'
      }
    `}>
      <span aria-hidden className={`h-1 w-1 rounded-full ${isActive ? 'bg-amber animate-pulse' : 'bg-fg-muted'}`} />
      {isActive ? 'Active' : 'Pending'}
    </span>
  );
}

/* ─────────── product card ─────────── */

function ProdCard({ item, onDelete }) {
  return (
    <article
      className="
        group relative flex flex-col overflow-hidden
        rounded-2xl border border-border-subtle bg-surface-paper
        transition-[border-color,transform,box-shadow] duration-200 ease-out-soft
        hover:border-amber/30 hover:-translate-y-1 hover:shadow-card-hover
      "
    >
      <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/30 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 z-10" />

      <div className="relative aspect-[16/10] overflow-hidden bg-surface-overlay">
        {item.product_image ? (
          <img
            src={item.product_image}
            alt={item.product_name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out-soft group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-fg-muted text-[12px] font-bold tracking-[0.2em]">
            ESAY
          </div>
        )}
        <div className="absolute top-2 right-2">
          <StatusPill status={item.product_status} />
        </div>
      </div>

      <div className="p-3.5 flex flex-col gap-2">
        <h3 className="text-[14.5px] font-bold tracking-[-0.01em] text-fg-primary line-clamp-2 leading-[1.3]">
          {item.product_name}
        </h3>
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-amber truncate">
          {item.sub_category_name}
        </div>
        <div className="flex items-center gap-1 text-[10.5px] text-fg-tertiary">
          <Icon.Calendar className="h-2.5 w-2.5" />
          <span>Added {item.product_register_date}</span>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <RouterLink
            to={`/myaccount/edit_prods_servs/${item.product_id}`}
            className="
              inline-flex h-8 items-center gap-1.5 rounded-lg px-3
              text-[10.5px] font-bold uppercase tracking-[0.1em]
              border border-amber/40 bg-amber/10 text-amber
              transition-[background-color,border-color] duration-200
              hover:bg-amber/20 hover:border-amber/60
            "
          >
            <Icon.Edit className="h-3 w-3" />
            Edit
          </RouterLink>
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete listing"
            className="
              inline-flex h-8 items-center gap-1.5 rounded-lg px-3
              text-[10.5px] font-bold uppercase tracking-[0.1em]
              border border-border-subtle text-fg-tertiary
              transition-[background-color,border-color,color] duration-200
              hover:border-red-400/40 hover:text-red-400 hover:bg-red-400/8
            "
          >
            <Icon.Trash className="h-3 w-3" />
            Delete
          </button>
        </div>
      </div>
    </article>
  );
}

/* ─────────── component ─────────── */

export const MyAccMyProds = () => {
  const history = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);
  const [data, setData] = useState([]);
  const [wait] = useState(false);

  const [deleteId, setDeleteId] = useState('');
  const [deleteName, setDeleteName] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [showPlan, setShowPlan] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => { getData(); }, []); // eslint-disable-line

  const getData = () => {
    const formData = { uid: user.user_id, cat: user.category_ids, role: user.user_role };
    Axios.post('/new_apis/getvendorproducts', formData, { headers: { tkn: user.token } }).then((res) => {
      if (res.data.status === 1) {
        setData(res.data.data);
        setShowPlan(res.data.substatus);
        setLoading(false);
      } else if (res.data.status === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      } else if (res.data.status === 4) {
        setUser(null);
        history('/');
        localStorage.removeItem('estkn');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      }
    });
  };

  const deleteProduct = () => {
    setLoading(true);
    const formData = { role: user.user_role, cat: user.category_ids, uid: user.user_id, pid: deleteId };
    Axios.post('/new_apis/deletevendorproduct', formData, { headers: { tkn: user.token } }).then((res) => {
      if (res.data.status === 1) {
        setSnack({ message: res.data.msg, type: 'success', open: true, direction: 'center' });
        getData();
      } else if (res.data.status === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      } else if (res.data.status === 4) {
        setUser(null);
        localStorage.removeItem('estkn');
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      }
    });
  };

  if (loading) return <Loading />;

  return (
    <>
      <BackDropLoad wait={wait} />

      <Modal
        open={showPlan}
        onClose={() => setShowPlan(false)}
        title="Choose a plan"
        size="sm"
        footer={(
          <>
            <GhostButton onClick={() => setShowPlan(false)}>Close</GhostButton>
            <PrimaryButton
              onClick={() => { setShowPlan(false); history('/myaccount/subscription'); }}
              icon={<Icon.ArrowRight className="h-4 w-4" />}
            >
              Subscribe now
            </PrimaryButton>
          </>
        )}
      >
        Choose a subscription plan to join the ESAY community and reach your desired customers.
      </Modal>

      <Modal
        open={showAlert}
        onClose={() => setShowAlert(false)}
        title="Please confirm"
        size="sm"
        footer={(
          <>
            <GhostButton onClick={() => setShowAlert(false)}>No</GhostButton>
            <PrimaryButton
              onClick={() => { setShowAlert(false); deleteProduct(); }}
              icon={<Icon.Trash className="h-4 w-4" />}
            >
              Yes, delete
            </PrimaryButton>
          </>
        )}
      >
        Are you sure you want to delete &ldquo;{deleteName}&rdquo;?
      </Modal>

      <AccountHeader
        kicker="Account"
        title="My products / services"
        subtitle="Manage the products and services you have listed on ESAY."
        action={
          data.length > 0 && data.length < 3 && (
            <RouterLink
              to="/myaccount/add_prods_servs"
              className="
                inline-flex h-10 items-center gap-1.5 rounded-lg px-4
                text-[11.5px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                transition-[background-color,box-shadow] duration-200
                hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
              "
            >
              <Icon.Add className="h-4 w-4" />
              Add new
            </RouterLink>
          )
        }
      />

      {data.length === 0 ? (
        <EmptyBlock
          icon={<Icon.Tag className="h-7 w-7" />}
          title="No products yet"
          subtitle="You have not added any products or services. Add one to start reaching customers."
          action={
            <RouterLink
              to="/myaccount/add_prods_servs"
              className="
                inline-flex h-10 items-center gap-1.5 rounded-lg px-4
                text-[12px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                transition-[background-color,box-shadow] duration-200
                hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
              "
            >
              <Icon.Add className="h-4 w-4" />
              Add product / service
            </RouterLink>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {data.map((item) => (
            <ProdCard
              key={item.product_id}
              item={item}
              onDelete={() => {
                setDeleteName(item.product_name);
                setDeleteId(item.product_id);
                setShowAlert(true);
              }}
            />
          ))}
        </div>
      )}
    </>
  );
};
